/** Weekly goal in minutes: parsing the field, progress numbers, formatting. */
import { t } from "../i18n";
import { parseDuration } from "./activity";

export const MAX_GOAL_MINUTES = 6000;
export const MAX_GOAL_COUNT = 14;

export type GoalParse =
  | { ok: true; value: number | null }
  | { ok: false; error: "format" | "range" };

const UNIT_FORM =
  /^(?:(\d+(?:[.,]\d+)?)\s*(?:ч|h|hr|hrs|hour|hours|час|часа|часов))?\s*(?:(\d+)\s*(?:м|m|min|mins|мин|минут|минуты))?$/i;

/**
 * "150", "2:30" (h:mm), "2ч 30м", "2h 30m", "1.5ч", "45 мин" -> whole minutes.
 * Empty = no goal (`null`). Bare numbers are minutes, like every duration field.
 */
export function parseGoalMinutes(raw: string): GoalParse {
  const s = raw.trim();
  if (!s) return { ok: true, value: null };

  let minutes: number | null = null;
  if (/^\d+$/.test(s) || /^\d+:\d{2}(:\d{2})?$/.test(s)) {
    const p = parseDuration(s, "hm");
    if (!p.ok) return { ok: false, error: p.error === "range" ? "range" : "format" };
    minutes = Math.round(p.value / 60);
  } else {
    const m = s.match(UNIT_FORM);
    if (!m || (m[1] == null && m[2] == null)) return { ok: false, error: "format" };
    const h = m[1] ? Number(m[1].replace(",", ".")) : 0;
    minutes = Math.round(h * 60 + (m[2] ? Number(m[2]) : 0));
  }

  if (!Number.isFinite(minutes) || minutes < 1 || minutes > MAX_GOAL_MINUTES) {
    return { ok: false, error: "range" };
  }
  return { ok: true, value: minutes };
}

export function goalErrorText(error: "format" | "range"): string {
  return error === "range" ? t("GOAL_MINUTES_RANGE") : t("ERR_FORMAT_HM");
}

/** 0..100, safe for zero/negative/NaN input. */
export function progressPercent(value: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((value / max) * 100)));
}

/** "95 / 150 мин" */
export function formatMinutesProgress(done: number, goal: number): string {
  return `${Math.round(done)} / ${Math.round(goal)} ${t("UNIT_MINUTES")}`;
}

/** Minutes progress to show, or null (no goal, or the backend does not send it). */
export function minutesProgress(
  p: { weeklyGoalMinutes?: number | null; weeklyMinutes?: number } | null | undefined,
): { done: number; goal: number } | null {
  const goal = p?.weeklyGoalMinutes;
  const done = p?.weeklyMinutes;
  if (typeof goal !== "number" || goal <= 0) return null;
  if (typeof done !== "number" || !Number.isFinite(done)) return null;
  return { done: Math.max(0, done), goal };
}

/** Count goal field: empty = not set (null), otherwise an integer 1..14. */
export function parseGoalCount(raw: string): { ok: true; value: number | null } | { ok: false } {
  const s = raw.trim();
  if (!s) return { ok: true, value: null };
  const n = Number(s);
  if (!/^\d+$/.test(s) || n < 1 || n > MAX_GOAL_COUNT) return { ok: false };
  return { ok: true, value: n };
}

export interface GoalSaveInput {
  /** Backend sends weeklyGoalMinutes (false = old backend: count only, as before). */
  supported: boolean;
  storedCount: number | null;
  storedMinutes: number | null;
  countText: string;
  countDirty: boolean;
  minutesText: string;
  minutesDirty: boolean;
}

export type GoalSavePlan =
  | { ok: true; body: { targetCount?: number | null; weeklyGoalMinutes?: number | null } | null }
  | { ok: false; count?: string; minutes?: string; general?: string };

/**
 * What to PUT. Only the goals the user changed are sent, so saving one never
 * resets the other (omitted = keep, null = clear). `body: null` = nothing to save.
 */
export function planGoalSave(i: GoalSaveInput): GoalSavePlan {
  const cp = parseGoalCount(i.countText);
  const mp = parseGoalMinutes(i.minutesText);

  if (!i.supported) {
    // Old backend: the count goal alone, exactly as before.
    if (!cp.ok || cp.value == null) return { ok: false, count: t("GOAL_COUNT_RANGE") };
    return { ok: true, body: { targetCount: cp.value } };
  }
  if (i.countDirty && !cp.ok) return { ok: false, count: t("GOAL_COUNT_RANGE") };
  if (i.minutesDirty && !mp.ok) return { ok: false, minutes: goalErrorText(mp.error) };

  const body: { targetCount?: number | null; weeklyGoalMinutes?: number | null } = {};
  if (i.countDirty && cp.ok && cp.value !== i.storedCount) body.targetCount = cp.value;
  if (i.minutesDirty && mp.ok && mp.value !== i.storedMinutes) body.weeklyGoalMinutes = mp.value;
  if (!("targetCount" in body) && !("weeklyGoalMinutes" in body)) return { ok: true, body: null };

  const count = "targetCount" in body ? body.targetCount : i.storedCount;
  const minutes = "weeklyGoalMinutes" in body ? body.weeklyGoalMinutes : i.storedMinutes;
  if (count == null && minutes == null) return { ok: false, general: t("GOAL_NEED_ONE") };
  return { ok: true, body };
}
